import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, throwError } from 'rxjs';
import { EligibilityEventHistory, APPLICATION_HISTORY_FILTER_TYPE } from '@edassist/api';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { constants } from '@edassist/common';

@Injectable({
	providedIn: 'root'
})
export class ApplicationHistoryService {
	private readonly eligibilityEventFilters = {
		[APPLICATION_HISTORY_FILTER_TYPE.COMMENTS]: [constants.messageTypes.message],
		[APPLICATION_HISTORY_FILTER_TYPE.STATUSES]: [
			constants.messageTypes.rulesMessage,
			constants.messageTypes.appStatusChange,
			constants.messageTypes.systemMessage
		],
		[APPLICATION_HISTORY_FILTER_TYPE.APPEAL]: [
			constants.messageTypes.appealReason,
			constants.messageTypes.appealStatusChange,
			constants.messageTypes.applicationException,
			constants.messageTypes.participantException,
			constants.messageTypes.applicationExceptionDeleted,
			constants.messageTypes.rulesMessage
		],
		[APPLICATION_HISTORY_FILTER_TYPE.EXCEPTIONS]: [
			constants.messageTypes.applicationException,
			constants.messageTypes.participantException,
			constants.messageTypes.applicationExceptionDeleted
		]
	};

	private currentApplicationId: number;

	private statusItemsLoaded: boolean = false;
	private rulesItemsLoaded: boolean = false;
	private otherStatusItemsLoaded: boolean = false;
	private appealItemsLoaded: boolean = false;
	private applicationExceptionItemsLoaded: boolean = false;
	private participantExceptionItemsLoaded: boolean = false;
	private commentsItemsLoaded: boolean = false;

	private hasAppeals: boolean = false;

	private readonly statusItemsSubject: BehaviorSubject<Array<EligibilityEventHistory>>;
	private readonly rulesItemsSubject: BehaviorSubject<Array<EligibilityEventHistory>>;
	private readonly otherStatusItemsSubject: BehaviorSubject<Array<EligibilityEventHistory>>;
	private readonly appealItemsSubject: BehaviorSubject<Array<EligibilityEventHistory>>;
	private readonly applicationExceptionItemsSubject: BehaviorSubject<Array<EligibilityEventHistory>>;
	private readonly participantExceptionItemsSubject: BehaviorSubject<Array<EligibilityEventHistory>>;
	private readonly commentsItemsSubject: BehaviorSubject<Array<EligibilityEventHistory>>;

	private readonly currentFilterSubject: BehaviorSubject<APPLICATION_HISTORY_FILTER_TYPE>;

	private readonly relativeUrl = 'applications/';

	constructor(private readonly http: HttpClient) {
		this.currentFilterSubject = new BehaviorSubject<APPLICATION_HISTORY_FILTER_TYPE>(
			APPLICATION_HISTORY_FILTER_TYPE.STATUSES
		);

		this.statusItemsSubject = new BehaviorSubject<Array<EligibilityEventHistory>>(undefined);
		this.rulesItemsSubject = new BehaviorSubject<Array<EligibilityEventHistory>>(undefined);
		this.otherStatusItemsSubject = new BehaviorSubject<Array<EligibilityEventHistory>>(undefined);
		this.appealItemsSubject = new BehaviorSubject<Array<EligibilityEventHistory>>(undefined);
		this.applicationExceptionItemsSubject = new BehaviorSubject<Array<EligibilityEventHistory>>(undefined);
		this.participantExceptionItemsSubject = new BehaviorSubject<Array<EligibilityEventHistory>>(undefined);
		this.commentsItemsSubject = new BehaviorSubject<Array<EligibilityEventHistory>>(undefined);
	}

	setCurrentFilter(filterType: APPLICATION_HISTORY_FILTER_TYPE) {
		this.currentFilterSubject.next(filterType);
	}

	getCurrentFilter(): APPLICATION_HISTORY_FILTER_TYPE {
		return this.currentFilterSubject.value;
	}

	setHasAppeals(hasAppeals: boolean): void {
		this.hasAppeals = hasAppeals;
	}

	getApplicationStatusHistory(
		applicationId: number,
		participantId: number,
		clientId: number,
		forceReload: boolean = false
	): Observable<Array<EligibilityEventHistory>> {
		if (forceReload) {
			this.resetForFullRefresh();
		}
		if (this.currentApplicationId !== applicationId) {
			this.applicationChanged(applicationId);
		}

		switch (this.currentFilterSubject.value) {
			case APPLICATION_HISTORY_FILTER_TYPE.STATUSES:
				return forkJoin([
					this.getStatusItems(applicationId),
					this.getRulesItems(applicationId),
					this.getCommentsItems(applicationId)
				]).pipe(
					catchError((error: any) => {
						return throwError(() => error);
					}),
					switchMap(([statuses, rules, comments]) => {
						return of(
							this.filterItems(
								this.groupStatusChangesWithComments(
									this.removeDuplicateEventHistoryItems([...statuses, ...rules, ...comments])
								),
								this.eligibilityEventFilters[APPLICATION_HISTORY_FILTER_TYPE.STATUSES]
							)
						);
					})
				);
			case APPLICATION_HISTORY_FILTER_TYPE.APPEAL:
				return forkJoin([
					this.getAppealItems(applicationId, clientId, participantId),
					this.getApplicationExceptionsItems(applicationId, clientId),
					this.getParticipantExceptionsItems(participantId, clientId),
					this.getRulesItems(applicationId),
					this.getCommentsItems(applicationId)
				]).pipe(
					catchError((error: any) => {
						return throwError(() => error);
					}),
					switchMap(([appeals, applicationExceptions, participantExceptions, rules, comments]) => {
						return of(
							this.filterItems(
								this.groupStatusChangesWithComments(
									this.removeDuplicateEventHistoryItems([
										...appeals,
										...applicationExceptions,
										...participantExceptions,
										...rules,
										...comments
									])
								),
								this.eligibilityEventFilters[APPLICATION_HISTORY_FILTER_TYPE.APPEAL]
							)
						);
					})
				);
			case APPLICATION_HISTORY_FILTER_TYPE.EXCEPTIONS:
				return forkJoin([
					this.getApplicationExceptionsItems(applicationId, clientId),
					this.getParticipantExceptionsItems(participantId, clientId)
				]).pipe(
					catchError((error: any) => {
						return throwError(() => error);
					}),
					switchMap(([applicationExceptions, participantExceptions]) => {
						return of(
							this.filterItems(
								this.removeDuplicateEventHistoryItems([...applicationExceptions, ...participantExceptions]),
								this.eligibilityEventFilters[APPLICATION_HISTORY_FILTER_TYPE.EXCEPTIONS]
							)
						);
					})
				);
			case APPLICATION_HISTORY_FILTER_TYPE.COMMENTS:
				return forkJoin([
					this.getCommentsItems(applicationId),
					this.getAppealItems(applicationId, clientId, participantId),
					this.getRulesItems(applicationId),
					this.getStatusItems(applicationId)
				]).pipe(
					catchError((error: any) => {
						return throwError(() => error);
					}),
					switchMap(([comments, appeals, rules, statuses]) => {
						return of(
							this.filterItems(
								this.groupStatusChangesWithComments(
									this.removeDuplicateEventHistoryItems([...comments, ...appeals, ...rules, ...statuses])
								),
								this.eligibilityEventFilters[APPLICATION_HISTORY_FILTER_TYPE.COMMENTS]
							)
						);
					})
				);

			case APPLICATION_HISTORY_FILTER_TYPE.ALL:
			default:
				return forkJoin([
					this.getStatusItems(applicationId),
					this.getOtherStatusItems(applicationId),
					this.getAppealItems(applicationId, clientId, participantId),
					this.getApplicationExceptionsItems(applicationId, clientId),
					this.getCommentsItems(applicationId),
					this.getRulesItems(applicationId)
				]).pipe(
					catchError((error: any) => {
						return throwError(() => error);
					}),
					switchMap(([statuses, otherStatuses, appeals, applicationExceptions, comments, rules]) => {
						return of(
							this.groupStatusChangesWithComments(
								this.removeDuplicateEventHistoryItems([
									...statuses,
									...otherStatuses,
									...appeals,
									...applicationExceptions,
									...comments,
									...rules
								])
							)
						);
					})
				);
		}
	}

	groupStatusChangesWithComments(history: Array<EligibilityEventHistory>): Array<EligibilityEventHistory> {
		if (!history) {
			return [];
		}

		history.forEach((item, index, arr) => {
			if (
				item.source?.groupId === constants.messageTypes.appStatusChange ||
				item.source?.groupId === constants.messageTypes.appealStatusChange
			) {
				// Find the related comment for the current statusChange
				const relatedMessageIndex = arr.findIndex(
					(msg) =>
						msg.source?.groupId === constants.messageTypes.message &&
						msg.statusId === item.statusId &&
						msg.createdByUserId === item.createdByUserId &&
						Math.abs(msg?.dateCreated - item?.dateCreated) < 3000 &&
						(msg.edassistComment || item.source?.groupId === constants.messageTypes.appealStatusChange) &&
						!msg.grouped
				);
				// If found, add it to the statusChange and mark it as grouped
				if (relatedMessageIndex !== -1) {
					item.statusChangeComment = arr[relatedMessageIndex];
					arr[relatedMessageIndex].grouped = true;
				}
			}
		});

		// Filter out grouped messages
		return history.filter(
			(item) =>
				!item.grouped ||
				item.source?.groupId === constants.messageTypes.appStatusChange ||
				item.source?.groupId === constants.messageTypes.appealStatusChange
		);
	}

	private resetForFullRefresh(): void {
		this.statusItemsLoaded = false;
		this.rulesItemsLoaded = false;
		this.otherStatusItemsLoaded = false;
		this.appealItemsLoaded = false;
		this.applicationExceptionItemsLoaded = false;
		this.participantExceptionItemsLoaded = false;
		this.commentsItemsLoaded = false;

		this.statusItemsSubject.next(undefined);
		this.rulesItemsSubject.next(undefined);
		this.otherStatusItemsSubject.next(undefined);
		this.appealItemsSubject.next(undefined);
		this.applicationExceptionItemsSubject.next(undefined);
		this.participantExceptionItemsSubject.next(undefined);
		this.commentsItemsSubject.next(undefined);
	}

	private applicationChanged(applicationId: number): void {
		this.currentApplicationId = applicationId;
		this.setCurrentFilter(APPLICATION_HISTORY_FILTER_TYPE.STATUSES);
		this.resetForFullRefresh();
	}

	private getStatusItems(applicationId: number): Observable<Array<EligibilityEventHistory>> {
		if (this.statusItemsLoaded) {
			return of(this.statusItemsSubject.value);
		} else {
			return this.http
				.get<
					Array<EligibilityEventHistory>
				>(`${this.relativeUrl}${applicationId.toString()}/ia-app-status-change-history`)
				.pipe(
					catchError((error: HttpErrorResponse) => {
						return throwError(() => error);
					}),
					map((res) => res || []),
					switchMap((items) => {
						this.statusItemsLoaded = true;
						this.statusItemsSubject.next(items);

						return of(items);
					})
				);
		}
	}

	private getOtherStatusItems(applicationId: number): Observable<Array<EligibilityEventHistory>> {
		if (this.otherStatusItemsLoaded) {
			return of(this.otherStatusItemsSubject.value);
		} else {
			return this.http
				.get<Array<EligibilityEventHistory>>(`${this.relativeUrl}${applicationId.toString()}/ia-other-status-history`)
				.pipe(
					catchError((error: HttpErrorResponse) => {
						return throwError(() => error);
					}),
					map((res) => res || []),
					switchMap((items) => {
						this.otherStatusItemsLoaded = true;
						this.otherStatusItemsSubject.next(items);

						return of(items);
					})
				);
		}
	}

	private getRulesItems(applicationId: number): Observable<Array<EligibilityEventHistory>> {
		if (this.rulesItemsLoaded) {
			return of(this.rulesItemsSubject.value);
		} else {
			return this.http
				.get<Array<EligibilityEventHistory>>(`${this.relativeUrl}${applicationId.toString()}/ia-rules-status-history`)
				.pipe(
					catchError((error: HttpErrorResponse) => {
						return throwError(() => error);
					}),
					map((res) => res || []),
					switchMap((items) => {
						this.rulesItemsLoaded = true;
						this.rulesItemsSubject.next(items);

						return of(items);
					})
				);
		}
	}

	private getAppealItems(
		applicationId: number,
		clientId: number,
		participantId: number
	): Observable<Array<EligibilityEventHistory>> {
		if (!this.hasAppeals) {
			this.appealItemsSubject.next([]);

			return of(this.appealItemsSubject.value);
		} else if (this.appealItemsLoaded) {
			return of(this.appealItemsSubject.value);
		} else {
			return this.http
				.get<Array<EligibilityEventHistory>>(
					`${this.relativeUrl}${applicationId.toString()}/ia-appeal-status-history`,
					{
						params: {
							clientId,
							participantId
						}
					}
				)
				.pipe(
					catchError((error: HttpErrorResponse) => {
						return throwError(() => error);
					}),
					map((res) => res || []),
					switchMap((items) => {
						this.appealItemsLoaded = true;
						this.appealItemsSubject.next(items);

						return of(items);
					})
				);
		}
	}

	private getApplicationExceptionsItems(
		applicationId: number,
		clientId: number
	): Observable<Array<EligibilityEventHistory>> {
		if (this.applicationExceptionItemsLoaded) {
			return of(this.applicationExceptionItemsSubject.value);
		} else {
			return this.http
				.get<
					Array<EligibilityEventHistory>
				>(`${this.relativeUrl}${applicationId.toString()}/ia-app-exceptions-status-history`, { params: { clientId } })
				.pipe(
					map((res) => res || []),
					catchError((error: HttpErrorResponse) => {
						return throwError(() => error);
					}),
					switchMap((items) => {
						this.applicationExceptionItemsLoaded = true;
						this.applicationExceptionItemsSubject.next(items);

						return of(items);
					})
				);
		}
	}

	private getParticipantExceptionsItems(
		participantId: number,
		clientId: number
	): Observable<Array<EligibilityEventHistory>> {
		if (this.participantExceptionItemsLoaded) {
			return of(this.participantExceptionItemsSubject.value);
		} else {
			return this.http
				.get<
					Array<EligibilityEventHistory>
				>(`${this.relativeUrl}/ia-participant-exceptions-status-history`, { params: { clientId, participantId } })
				.pipe(
					map((res) => res || []),
					catchError((error: HttpErrorResponse) => {
						return throwError(() => error);
					}),
					switchMap((items) => {
						this.participantExceptionItemsLoaded = true;
						this.participantExceptionItemsSubject.next(items);

						return of(items);
					})
				);
		}
	}

	private getCommentsItems(applicationId: number): Observable<Array<EligibilityEventHistory>> {
		if (this.commentsItemsLoaded) {
			return of(this.commentsItemsSubject.value);
		} else {
			return this.http
				.get<Array<EligibilityEventHistory>>(`${this.relativeUrl}${applicationId.toString()}/ia-message-status-history`)
				.pipe(
					catchError((error: HttpErrorResponse) => {
						return throwError(() => error);
					}),
					map((res) => res || []),
					switchMap((items) => {
						this.commentsItemsLoaded = true;
						this.commentsItemsSubject.next(items);

						return of(items);
					})
				);
		}
	}

	private removeDuplicateEventHistoryItems(items: EligibilityEventHistory[]): EligibilityEventHistory[] {
		if (items?.length) {
			return items.filter(
				(item, index, self) => index === self.findIndex((t) => t.source.entityId === item.source.entityId)
			);
		}

		return items;
	}

	private filterItems(items: EligibilityEventHistory[], filter: Array<number>): EligibilityEventHistory[] {
		return items.filter((item) => {
			return filter.indexOf(item?.source?.groupId) > -1;
		});
	}
}
